/**
 * EVENTSFLO-LIVE-RESTORE-PHASE-5-ADMIN-UI-V1
 *
 * SuperAdmin-only Live Restore Cutover Wizard. Drives the Phase 5
 * backend endpoints (`/api/admin/backup/live-restore/{preflight,
 * apply,runs}`) which are themselves **metadata-only** in the
 * current pod topology — automated DB swap is NOT supported.
 *
 * UI safety contract:
 *   - Hidden for non-SuperAdmin (returns `null`).
 *   - Apply is disabled until the operator types the exact
 *     confirmation phrase `PROMOTE ISOLATED RESTORE TO LIVE`.
 *   - Apply button label is `Prepare Manual Cutover Plan` — NEVER
 *     anything that implies an automatic live restore.
 *   - Blockers from preflight visibly disable Apply.
 *   - Backend errors render safely; no raw stack traces / URIs.
 *   - No clear-text Mongo URI / host / password is ever rendered;
 *     the backend returns leak-safe `db:<name>` labels only.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  AlertTriangle,
  CheckCircle2,
  ClipboardCopy,
  Database,
  Info,
  Play,
  RefreshCw,
  ShieldAlert,
  XCircle,
} from "lucide-react";
import { useAuth } from "../../../context/AuthContext";

const CONFIRM_PHRASE = "PROMOTE ISOLATED RESTORE TO LIVE";
const ENDPOINT = "/api/admin/backup/live-restore";

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return String(iso);
  }
}

async function authedFetch(path, token, options = {}) {
  const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  let body = null;
  try {
    body = await r.json();
  } catch {
    body = null;
  }
  if (!r.ok) {
    // Always surface the backend's structured error if present.
    const detail = body?.detail || body || { error: `HTTP ${r.status}` };
    const msg = typeof detail === "string" ? detail : (detail.message || detail.error || `HTTP ${r.status}`);
    const err = new Error(msg);
    err.detail = detail;
    err.status = r.status;
    throw err;
  }
  return body;
}

function StatusBadge({ status, canApply }) {
  if (canApply === true) {
    return (
      <Badge
        className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
        data-testid="cutover-can-apply-badge"
      >
        Can apply
      </Badge>
    );
  }
  if (canApply === false) {
    return (
      <Badge
        className="bg-rose-500/15 text-rose-300 border border-rose-500/30"
        data-testid="cutover-cannot-apply-badge"
      >
        Cannot apply
      </Badge>
    );
  }
  if (status === "pending_manual") {
    return (
      <Badge
        className="bg-amber-500/15 text-amber-300 border border-amber-500/30"
        data-testid="cutover-pending-manual-badge"
      >
        Pending manual cutover
      </Badge>
    );
  }
  return null;
}

function BlockersList({ blockers, warnings }) {
  if (!blockers?.length && !warnings?.length) return null;
  return (
    <div className="space-y-2 mt-3">
      {blockers?.length > 0 ? (
        <div
          className="rounded-md border border-rose-500/30 bg-rose-500/5 p-3"
          data-testid="cutover-blockers"
        >
          <div className="flex items-center gap-2 text-rose-300 text-sm font-medium">
            <ShieldAlert className="w-4 h-4" /> Blockers ({blockers.length})
          </div>
          <ul className="mt-1 list-disc list-inside text-rose-200 text-xs space-y-0.5">
            {blockers.map((b) => (
              <li key={b} data-testid={`cutover-blocker-${b}`}>
                {b}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {warnings?.length > 0 ? (
        <div
          className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3"
          data-testid="cutover-warnings"
        >
          <div className="flex items-center gap-2 text-amber-300 text-sm font-medium">
            <AlertTriangle className="w-4 h-4" /> Warnings ({warnings.length})
          </div>
          <ul className="mt-1 list-disc list-inside text-amber-200 text-xs space-y-0.5">
            {warnings.map((w, i) => (
              <li key={`${w}-${i}`}>{w}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function ComparisonTable({ rows }) {
  if (!rows?.length) return null;
  return (
    <div
      className="overflow-auto rounded border border-zinc-700/40 mt-3"
      data-testid="cutover-comparison-table"
    >
      <table className="w-full text-xs">
        <thead className="bg-zinc-800/60 text-zinc-400">
          <tr>
            <th className="text-left px-2 py-1">collection</th>
            <th className="text-right px-2 py-1">live</th>
            <th className="text-right px-2 py-1">isolated</th>
            <th className="text-right px-2 py-1">delta</th>
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 25).map((r) => (
            <tr key={r.collection} className="odd:bg-zinc-900/30">
              <td className="px-2 py-1 text-zinc-300">{r.collection}</td>
              <td className="px-2 py-1 text-right text-zinc-300">{r.live}</td>
              <td className="px-2 py-1 text-right text-zinc-300">{r.isolated}</td>
              <td
                className={`px-2 py-1 text-right ${
                  r.delta < 0 ? "text-rose-300" : r.delta > 0 ? "text-emerald-300" : "text-zinc-400"
                }`}
              >
                {r.delta > 0 ? "+" : ""}
                {r.delta}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ManualPlan({ steps, onCopy }) {
  if (!steps?.length) return null;
  return (
    <div
      className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 mt-3"
      data-testid="cutover-manual-plan"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-amber-300 text-sm font-medium">
          <Info className="w-4 h-4" /> Manual SRE cutover plan
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={onCopy}
          data-testid="cutover-copy-runbook-btn"
        >
          <ClipboardCopy className="w-3.5 h-3.5 mr-1" /> Copy runbook
        </Button>
      </div>
      <ol className="mt-2 list-decimal list-inside text-amber-200 text-xs space-y-1">
        {steps.map((s, i) => (
          <li key={i}>{s}</li>
        ))}
      </ol>
      <div className="mt-3 text-xs text-amber-200/80">
        These steps must be executed by an SRE OUTSIDE the running
        backend process. No automatic live DB swap occurred. The
        previous live DB must be preserved until the manual cutover
        is validated.
      </div>
    </div>
  );
}

function RecentRuns({ data, loading, onRefresh }) {
  return (
    <div className="mt-4" data-testid="cutover-recent-runs">
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-medium text-zinc-300">Recent runs</div>
        <Button
          size="sm"
          variant="outline"
          onClick={onRefresh}
          disabled={loading}
          data-testid="cutover-refresh-runs-btn"
        >
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
        <div>
          <div className="text-zinc-400 mb-1">Preflight runs</div>
          <ul className="space-y-1" data-testid="cutover-preflight-runs">
            {(data?.preflight_runs || []).slice(0, 5).map((p) => (
              <li
                key={p.id}
                className="rounded border border-zinc-700/40 p-2"
              >
                <div className="flex items-center justify-between">
                  <span className="text-zinc-300 truncate">
                    {p.restore_run_id}
                  </span>
                  <Badge
                    className={
                      p.can_apply
                        ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                        : "bg-rose-500/15 text-rose-300 border border-rose-500/30"
                    }
                  >
                    {p.can_apply ? "can_apply" : "blocked"}
                  </Badge>
                </div>
                <div className="text-zinc-500 mt-0.5">
                  {p.actor_email} · {fmt(p.generated_at)}
                </div>
                {p.blockers?.length ? (
                  <div className="text-rose-300 mt-0.5">
                    blockers: {p.blockers.join(", ")}
                  </div>
                ) : null}
              </li>
            ))}
            {(!data?.preflight_runs || data.preflight_runs.length === 0) && (
              <li className="text-zinc-500">No preflight runs yet.</li>
            )}
          </ul>
        </div>
        <div>
          <div className="text-zinc-400 mb-1">Cutover runs</div>
          <ul className="space-y-1" data-testid="cutover-cutover-runs">
            {(data?.cutover_runs || []).slice(0, 5).map((c) => (
              <li
                key={c.id}
                className="rounded border border-zinc-700/40 p-2"
              >
                <div className="flex items-center justify-between">
                  <span className="text-zinc-300 truncate">
                    {c.restore_run_id}
                  </span>
                  <Badge className="bg-amber-500/15 text-amber-300 border border-amber-500/30">
                    {c.status}
                  </Badge>
                </div>
                <div className="text-zinc-500 mt-0.5">
                  {c.actor_email} · {fmt(c.started_at)}
                </div>
                <div className="text-zinc-500 mt-0.5">
                  {c.old_live_reference} → {c.new_live_reference}
                </div>
              </li>
            ))}
            {(!data?.cutover_runs || data.cutover_runs.length === 0) && (
              <li className="text-zinc-500">No cutover runs yet.</li>
            )}
          </ul>
        </div>
      </div>
      {data?.automated_cutover_supported === false ? (
        <div
          className="mt-3 text-xs text-zinc-500 flex items-start gap-2"
          data-testid="cutover-automated-unsupported-note"
        >
          <Info className="w-3.5 h-3.5 mt-0.5" />
          <span>
            Automated cutover is not supported in this deployment.
            Apply only prepares a manual SRE runbook. The previous
            live database is never deleted by this tool.
          </span>
        </div>
      ) : null}
    </div>
  );
}

export default function LiveRestoreCutoverCard() {
  const { user, token } = useAuth();
  const isSuperAdmin = user?.role === "superadmin";

  const [restoreRunId, setRestoreRunId] = useState("");
  const [backupId, setBackupId] = useState("");
  const [preflightLoading, setPreflightLoading] = useState(false);
  const [preflightError, setPreflightError] = useState(null);
  const [preflight, setPreflight] = useState(null);

  const [confirmInput, setConfirmInput] = useState("");
  const [applyLoading, setApplyLoading] = useState(false);
  const [applyError, setApplyError] = useState(null);
  const [applyResult, setApplyResult] = useState(null);

  const [runs, setRuns] = useState(null);
  const [runsLoading, setRunsLoading] = useState(false);

  const loadRuns = useCallback(async () => {
    if (!isSuperAdmin || !token) return;
    setRunsLoading(true);
    try {
      const body = await authedFetch(`${ENDPOINT}/runs`, token);
      setRuns(body);
    } catch (e) {
      // Recent runs is non-fatal; do NOT spam the toast.
      console.warn("live-restore runs load failed:", e?.message);
    } finally {
      setRunsLoading(false);
    }
  }, [isSuperAdmin, token]);

  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  const runPreflight = async () => {
    if (!restoreRunId.trim()) {
      toast.error("restore_run_id is required");
      return;
    }
    setPreflightLoading(true);
    setPreflightError(null);
    setApplyResult(null);
    setApplyError(null);
    try {
      const body = await authedFetch(`${ENDPOINT}/preflight`, token, {
        method: "POST",
        body: JSON.stringify({
          restore_run_id: restoreRunId.trim(),
          backup_id: backupId.trim() || undefined,
        }),
      });
      setPreflight(body);
      if (body.can_apply) {
        toast.success("Preflight passed — Apply unlocked behind confirm phrase");
      } else {
        toast.warning(
          `Preflight has ${body.blockers?.length || 0} blocker(s). Apply disabled.`,
        );
      }
      loadRuns();
    } catch (e) {
      setPreflightError(e.message || "Preflight failed");
      setPreflight(null);
    } finally {
      setPreflightLoading(false);
    }
  };

  const applyCutover = async () => {
    if (!preflight?.preflight_run_id) return;
    if (!preflight.can_apply) return;
    if (confirmInput !== CONFIRM_PHRASE) return;
    setApplyLoading(true);
    setApplyError(null);
    try {
      const body = await authedFetch(`${ENDPOINT}/apply`, token, {
        method: "POST",
        body: JSON.stringify({
          preflight_run_id: preflight.preflight_run_id,
          restore_run_id: preflight.restore_run_id,
          backup_id: preflight.backup_id,
          confirm: CONFIRM_PHRASE,
        }),
      });
      setApplyResult(body);
      toast.success("Cutover intent recorded — execute the manual plan now");
      loadRuns();
    } catch (e) {
      setApplyError(e.message || "Apply failed");
    } finally {
      setApplyLoading(false);
    }
  };

  const onCopyRunbook = () => {
    if (!applyResult?.manual_cutover_plan?.length) return;
    const text = [
      `EventsFlo Live-Restore Manual Cutover Runbook`,
      `cutover_run_id: ${applyResult.cutover_run_id}`,
      `rollback_reference: ${applyResult.rollback_reference}`,
      `old_live_reference: ${applyResult.old_live_reference}`,
      `new_live_reference: ${applyResult.new_live_reference}`,
      `status: ${applyResult.status}`,
      ``,
      ...applyResult.manual_cutover_plan,
    ].join("\n");
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(
        () => toast.success("Runbook copied to clipboard"),
        () => toast.error("Clipboard unavailable"),
      );
    } else {
      // Graceful degrade: render into a window.prompt so the operator
      // can manually copy the runbook even in browsers/contexts
      // without `navigator.clipboard`.
      window.prompt("Copy the runbook text:", text);
    }
  };

  const exactMatch = useMemo(
    () => confirmInput === CONFIRM_PHRASE,
    [confirmInput],
  );

  if (!isSuperAdmin) return null;

  const canShowApply = !!preflight && preflight.can_apply === true;

  return (
    <Card data-testid="live-restore-cutover-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Database className="w-4 h-4" /> Live Restore Cutover
        </CardTitle>
        <p className="text-xs text-zinc-400 mt-1">
          Runs a safe preflight against an isolated restore and
          prepares a manual SRE cutover plan. This does not
          automatically swap the live database.
        </p>
      </CardHeader>
      <CardContent>
        {/* Step 1 — restore_run_id + Run Preflight */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="cutover-restore-run-id" className="text-xs text-zinc-400">
                restore_run_id
              </Label>
              <Input
                id="cutover-restore-run-id"
                value={restoreRunId}
                onChange={(e) => setRestoreRunId(e.target.value)}
                placeholder="restore_session_id or run id"
                data-testid="cutover-restore-run-id-input"
              />
            </div>
            <div>
              <Label htmlFor="cutover-backup-id" className="text-xs text-zinc-400">
                backup_id (optional)
              </Label>
              <Input
                id="cutover-backup-id"
                value={backupId}
                onChange={(e) => setBackupId(e.target.value)}
                placeholder="backup_<...>"
                data-testid="cutover-backup-id-input"
              />
            </div>
          </div>
          <Button
            onClick={runPreflight}
            disabled={preflightLoading || !restoreRunId.trim()}
            data-testid="cutover-run-preflight-btn"
          >
            <Play className="w-3.5 h-3.5 mr-1" />
            {preflightLoading ? "Running…" : "Run preflight"}
          </Button>
        </div>

        {preflightError ? (
          <div
            className="mt-3 rounded-md border border-rose-500/30 bg-rose-500/5 p-3 text-xs text-rose-300 flex items-start gap-2"
            data-testid="cutover-preflight-error"
          >
            <XCircle className="w-4 h-4 mt-0.5" />
            <span>Preflight error: {preflightError}</span>
          </div>
        ) : null}

        {/* Step 2 — preflight summary */}
        {preflight ? (
          <div className="mt-4 space-y-2" data-testid="cutover-preflight-summary">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <StatusBadge canApply={preflight.can_apply} />
              <span className="text-zinc-400">·</span>
              <span className="text-zinc-300">
                Validation:{" "}
                <span data-testid="cutover-validation-status">{preflight.validation_status}</span>
              </span>
              <span className="text-zinc-400">·</span>
              <span className="text-zinc-300">
                Isolated reachable:{" "}
                <span data-testid="cutover-isolated-reachable">
                  {preflight.isolated_reachable ? "yes" : "no"}
                </span>
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
              <div>
                <div className="text-zinc-400">live</div>
                <div className="text-zinc-300" data-testid="cutover-live-db-label">
                  {preflight.live_db_name}
                </div>
              </div>
              <div>
                <div className="text-zinc-400">isolated</div>
                <div className="text-zinc-300" data-testid="cutover-isolated-db-label">
                  {preflight.isolated_db_name}
                </div>
              </div>
              <div>
                <div className="text-zinc-400">live docs</div>
                <div className="text-zinc-300">
                  {preflight.document_root_summary?.live_total_non_bootstrap ?? "—"}
                </div>
              </div>
              <div>
                <div className="text-zinc-400">isolated docs</div>
                <div className="text-zinc-300">
                  {preflight.document_root_summary?.isolated_total_non_bootstrap ?? "—"}
                </div>
              </div>
            </div>
            <BlockersList
              blockers={preflight.blockers}
              warnings={preflight.warnings}
            />
            <ComparisonTable rows={preflight.collection_count_comparison} />
            <div className="text-xs text-zinc-500">
              required confirm phrase:{" "}
              <code className="text-zinc-300">{preflight.required_confirm_phrase}</code>
            </div>
          </div>
        ) : null}

        {/* Step 3 — Apply guarded action */}
        {canShowApply ? (
          <div
            className="mt-4 rounded-md border border-zinc-700/40 p-3"
            data-testid="cutover-apply-section"
          >
            <div className="text-sm text-zinc-300 mb-2">
              Type the confirmation phrase exactly to unlock the
              manual cutover plan.
            </div>
            <div className="flex flex-col md:flex-row md:items-end gap-2">
              <div className="flex-1">
                <Label htmlFor="cutover-confirm-input" className="text-xs text-zinc-400">
                  Confirm phrase
                </Label>
                <Input
                  id="cutover-confirm-input"
                  value={confirmInput}
                  onChange={(e) => setConfirmInput(e.target.value)}
                  placeholder={CONFIRM_PHRASE}
                  data-testid="cutover-confirm-input"
                />
              </div>
              <Button
                variant="default"
                onClick={applyCutover}
                disabled={!exactMatch || applyLoading}
                data-testid="cutover-apply-btn"
              >
                {applyLoading ? "Preparing…" : "Prepare manual cutover plan"}
              </Button>
            </div>
            <div className="mt-2 text-xs text-zinc-500 flex items-start gap-2">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 text-amber-400" />
              <span>
                This does not automatically swap the live database.
                Old live DB must be preserved until the manual
                cutover is validated.
              </span>
            </div>
          </div>
        ) : null}

        {applyError ? (
          <div
            className="mt-3 rounded-md border border-rose-500/30 bg-rose-500/5 p-3 text-xs text-rose-300 flex items-start gap-2"
            data-testid="cutover-apply-error"
          >
            <XCircle className="w-4 h-4 mt-0.5" />
            <span>Apply error: {applyError}</span>
          </div>
        ) : null}

        {/* Step 4 — Apply result */}
        {applyResult ? (
          <div className="mt-4 space-y-2" data-testid="cutover-apply-result">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <StatusBadge status={applyResult.status} />
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              <span className="text-zinc-300">
                cutover_run_id:{" "}
                <code data-testid="cutover-run-id">{applyResult.cutover_run_id}</code>
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
              <div className="rounded border border-zinc-700/40 p-2">
                <div className="text-zinc-400">old_live_reference</div>
                <div className="text-zinc-300" data-testid="cutover-old-live-ref">
                  {applyResult.old_live_reference}
                </div>
              </div>
              <div className="rounded border border-zinc-700/40 p-2">
                <div className="text-zinc-400">new_live_reference</div>
                <div className="text-zinc-300" data-testid="cutover-new-live-ref">
                  {applyResult.new_live_reference}
                </div>
              </div>
              <div className="rounded border border-zinc-700/40 p-2">
                <div className="text-zinc-400">rollback_reference</div>
                <div className="text-zinc-300" data-testid="cutover-rollback-ref">
                  {applyResult.rollback_reference}
                </div>
              </div>
              <div className="rounded border border-zinc-700/40 p-2">
                <div className="text-zinc-400">post-cutover validation</div>
                <div className="text-zinc-300" data-testid="cutover-post-validation">
                  live_database_mutated:{" "}
                  {applyResult.post_cutover_validation?.live_database_mutated
                    ? "true"
                    : "false"}{" "}
                  · live_collection_count:{" "}
                  {applyResult.post_cutover_validation?.live_collection_count ?? "—"}
                </div>
              </div>
            </div>
            <ManualPlan
              steps={applyResult.manual_cutover_plan}
              onCopy={onCopyRunbook}
            />
          </div>
        ) : null}

        {/* Step 5 — Recent runs */}
        <RecentRuns
          data={runs}
          loading={runsLoading}
          onRefresh={loadRuns}
        />
      </CardContent>
    </Card>
  );
}
